import {
    AlertBadge,
    MetadataChip,
    OutcomeBadge
} from "@solomon-apps/shared/src/components/console";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import {
    TabsMenu,
    TabTrigger
} from "@solomon-apps/shared/src/components/ui/tabs-menu";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ToggleGroup,
    ToggleGroupItem
} from "@solomon-apps/shared/src/components/ui/toggle-group";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import { AnimatePresence, motion } from "motion/react";
import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import {
    useClientAccountDetail,
    useClientAccounts
} from "@/hooks/useClientAccounts";
import { UsersTable } from "@/pages/Accounts/components/UsersTable";
import { buildUserRows, type UserRow } from "@/pages/Accounts/userRows";
import { KeyStatsRow } from "@/pages/Dashboard/components/PortfolioKeyStats";
import type {
    ClientAccountDecisionTrailItem,
    ClientAccountDetailResponse,
    ClientAccountFilters,
    ClientAccountSettlementEvaluation,
    ParticipantAccountSettlementResult
} from "@/types/clientAccounts";

type PeriodView = "chart" | "table";

/** Users list filter — All / Rewarded / Needs attention. */
type FilterTab = "all" | "rewarded" | "needsAttention";

const FILTER_TABS: { value: FilterTab; label: string }[] = [
    { value: "all", label: "All" },
    { value: "rewarded", label: "Rewarded" },
    { value: "needsAttention", label: "Needs attention" }
];

const ACCOUNT_PARAM = "account";
const CYCLE_PARAM = "cycle";
const POLICY_PARAM = "policy";

const NO_SETTLEMENT_HISTORY_SUBTITLE =
    "No completed settlement has evaluated this account yet.";
const RAISED_SURFACE_BADGE_CLASS = "bg-surface-ground";
const ACCOUNT_DETAIL_SECTION_CLASS =
    "border border-line-base bg-surface-sunken";

function usd(value: number): string {
    return `$${value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
}

/** Recharts SVGs receive focus on click; table view is the keyboard path. */
const SETTLEMENT_CHART_SURFACE_CLASS = cn(
    "[&_.recharts-wrapper]:!outline-none [&_.recharts-wrapper]:focus:!outline-none [&_.recharts-wrapper]:focus-visible:!outline-none",
    "[&_.recharts-surface]:!outline-none [&_.recharts-surface]:focus:!outline-none [&_.recharts-surface]:focus-visible:!outline-none",
    "[&_svg]:!outline-none [&_svg]:focus:!outline-none [&_rect]:!outline-none [&_rect]:focus:!outline-none",
    "[&_[tabindex]]:!outline-none [&_[tabindex]:focus]:!outline-none [&_[tabindex]:focus-visible]:!outline-none",
    "[&_.recharts-tooltip-cursor]:stroke-none"
);

export function AccountsPage(): React.ReactElement {
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState<FilterTab>("all");
    const [periodView, setPeriodView] = useState<PeriodView>("chart");

    // List/detail are driven entirely by the `account` search param: no param
    // → Users table; param set → that user's detail. No server-side filters on
    // the list anymore (the table filters/sorts client-side).
    const filters = useMemo<ClientAccountFilters>(() => ({}), []);

    const { data, isLoading, error } = useClientAccounts(filters);
    const allRows = useMemo(() => buildUserRows(data?.groups), [data?.groups]);

    const requestedAccountUuid = searchParams.get(ACCOUNT_PARAM);
    const requestedCycleUuid = searchParams.get(CYCLE_PARAM);

    // Only resolve a selection from the URL — default to the list view.
    const selectedAccountUuid =
        requestedAccountUuid &&
        allRows.some((row) => row.uuid === requestedAccountUuid)
            ? requestedAccountUuid
            : null;

    const selectedCycleUuid = requestedCycleUuid || null;

    const detailFilters = useMemo(
        () => ({
            ...filters,
            ...(selectedCycleUuid ? { cycleUuid: selectedCycleUuid } : {})
        }),
        [filters, selectedCycleUuid]
    );
    const {
        data: accountDetail,
        isLoading: isDetailLoading,
        error: detailError
    } = useClientAccountDetail(selectedAccountUuid, detailFilters);

    const visibleRows = useMemo<UserRow[]>(() => {
        if (tab === "all") return allRows;
        if (tab === "rewarded") {
            return allRows.filter((row) => row.result === "Rewarded");
        }
        // Needs attention — not rewarded, or no settlement history yet.
        return allRows.filter((row) => row.result !== "Rewarded");
    }, [allRows, tab]);

    // Aggregate key stats across all users (independent of the visible tab).
    const totals = useMemo(() => {
        return allRows.reduce(
            (acc, row) => ({
                held: acc.held + row.balance,
                earned: acc.earned + row.earned,
                pending: acc.pending + row.pending
            }),
            { held: 0, earned: 0, pending: 0 }
        );
    }, [allRows]);

    const handleSelectAccount = (accountUuid: string): void => {
        const next = new URLSearchParams(searchParams);
        next.set(ACCOUNT_PARAM, accountUuid);
        next.delete(CYCLE_PARAM);
        setSearchParams(next);
    };

    const handleBackToUsers = (): void => {
        const next = new URLSearchParams(searchParams);
        next.delete(ACCOUNT_PARAM);
        next.delete(CYCLE_PARAM);
        setSearchParams(next);
    };

    const handleSelectCycle = (cycleUuid: string): void => {
        const next = new URLSearchParams(searchParams);
        if (selectedCycleUuid === cycleUuid) {
            next.delete(CYCLE_PARAM);
        } else {
            next.set(CYCLE_PARAM, cycleUuid);
        }
        setSearchParams(next);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="flex w-full flex-col pt-6"
        >
            {/* Page header */}
            <section className="pb-6">
                <div className="px-6 flex flex-col gap-4">
                    <Text variant="h1" className="text-foreground-base">
                        Users
                    </Text>
                </div>
            </section>

            {/* Full-bleed key-stats row. */}
            <KeyStatsRow
                stats={[
                    { label: "Total USDv Held", value: usd(totals.held) },
                    { label: "Total USDv Earned", value: usd(totals.earned) },
                    { label: "Total USDv Pending", value: usd(totals.pending) },
                    {
                        label: "Total Users",
                        value: allRows.length.toLocaleString("en-US")
                    }
                ]}
            />

            {selectedAccountUuid ? (
                <DetailSection
                    detailError={detailError}
                    accountDetail={accountDetail}
                    isDetailLoading={isDetailLoading}
                    periodView={periodView}
                    selectedCycleUuid={selectedCycleUuid}
                    onViewChange={setPeriodView}
                    onSelectCycle={handleSelectCycle}
                    onBack={handleBackToUsers}
                />
            ) : (
                <>
                    {/* Controls row — filter ToggleGroup. */}
                    <div className="px-6 py-5 flex items-center justify-between gap-4">
                        <ToggleGroup
                            variant="outline"
                            size="sm"
                            spacing={2}
                            value={[tab]}
                            onValueChange={(value) => {
                                const next = value[0] as FilterTab | undefined;
                                if (next) setTab(next);
                            }}
                        >
                            {FILTER_TABS.map((t) => (
                                <ToggleGroupItem
                                    key={t.value}
                                    value={t.value}
                                    className="px-3"
                                >
                                    {t.label}
                                </ToggleGroupItem>
                            ))}
                        </ToggleGroup>
                    </div>

                    {isLoading && allRows.length === 0 ? (
                        <div className="px-6 py-6">
                            <Skeleton className="h-4 w-32 rounded-none" />
                            <Skeleton className="h-3 w-64 rounded-none mt-3" />
                        </div>
                    ) : error ? (
                        <div className="px-6 py-6">
                            <ErrorShell
                                title="Users unavailable"
                                message={error.message}
                            />
                        </div>
                    ) : (
                        <UsersTable
                            users={visibleRows}
                            onSelect={handleSelectAccount}
                        />
                    )}
                </>
            )}
        </motion.div>
    );
}

/**
 * The selected-user detail view: a back affordance over the existing account
 * detail panel (settlement explorer, decision trail, Program Surfaces, screening).
 * All detail logic is preserved unchanged — only the surrounding chrome
 * (split layout → back button) differs from the old master/detail layout.
 */
function DetailSection({
    detailError,
    accountDetail,
    isDetailLoading,
    periodView,
    selectedCycleUuid,
    onViewChange,
    onSelectCycle,
    onBack
}: {
    detailError: Error | null;
    accountDetail: ClientAccountDetailResponse | null;
    isDetailLoading: boolean;
    periodView: PeriodView;
    selectedCycleUuid: string | null;
    onViewChange: (view: PeriodView) => void;
    onSelectCycle: (cycleUuid: string) => void;
    onBack: () => void;
}): React.ReactElement {
    return (
        <div className="flex flex-col gap-4 px-6 py-6">
            <Button
                variant="transparent"
                interaction="default"
                size="xs"
                onClick={onBack}
                className="self-start"
            >
                ← Back to users
            </Button>
            {detailError ? (
                <ErrorShell
                    title="Account unavailable"
                    message={detailError.message}
                />
            ) : (
                <AccountDetailPanel
                    detail={accountDetail}
                    isLoading={isDetailLoading}
                    view={periodView}
                    selectedCycleUuid={selectedCycleUuid}
                    onViewChange={onViewChange}
                    onSelectCycle={onSelectCycle}
                />
            )}
        </div>
    );
}

function AccountSettlementExplorer({
    history,
    latest,
    selectedPeriod,
    view,
    selectedCycleUuid,
    onViewChange,
    onSelectCycle
}: {
    history: ClientAccountSettlementEvaluation[];
    latest: ClientAccountSettlementEvaluation | null;
    selectedPeriod: ClientAccountSettlementEvaluation | null;
    view: PeriodView;
    selectedCycleUuid: string | null;
    onViewChange: (view: PeriodView) => void;
    onSelectCycle: (cycleUuid: string) => void;
}): React.ReactElement {
    return (
        <section
            className={ACCOUNT_DETAIL_SECTION_CLASS}
            aria-label="Selected account settlement periods"
        >
            <div className="flex flex-col gap-4 border-b border-line-base bg-surface-raised p-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                    <Heading
                        eyebrow="Account settlements"
                        title={
                            latest
                                ? `Latest: cycle ${latest.cycleNumber}`
                                : "No settlement history"
                        }
                        titleVariant="h2"
                        subtitle="Selected account only."
                        subtitleVariant="small"
                        className="gap-1"
                    />
                    {latest ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <ResultBadge result={latest.result} compact />
                            <MetadataChip
                                className={RAISED_SURFACE_BADGE_CLASS}
                            >
                                {formatUsd(toNumber(latest.distributedAmount))}{" "}
                                distributed
                            </MetadataChip>
                            <Text
                                variant="label"
                                className="text-foreground-muted"
                            >
                                {formatDate(latest.runAt)}
                            </Text>
                        </div>
                    ) : null}
                    {selectedCycleUuid && selectedPeriod ? (
                        <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-3 flex flex-wrap items-center gap-2"
                        >
                            <Text
                                variant="label"
                                className="text-foreground-muted"
                            >
                                Viewing:
                            </Text>
                            <MetadataChip
                                className={RAISED_SURFACE_BADGE_CLASS}
                            >
                                Cycle {selectedPeriod.cycleNumber}
                            </MetadataChip>
                            <Button
                                variant="transparent"
                                interaction="default"
                                size="xs"
                                onClick={() =>
                                    onSelectCycle(selectedPeriod.cycleUuid)
                                }
                            >
                                Clear cycle
                            </Button>
                        </motion.div>
                    ) : null}
                </div>
                <TabsMenu
                    value={view}
                    onValueChange={(value) => onViewChange(value as PeriodView)}
                >
                    <TabTrigger value="chart" size="xs">
                        Chart
                    </TabTrigger>
                    <TabTrigger value="table" size="xs">
                        Table
                    </TabTrigger>
                </TabsMenu>
            </div>
            {history.length === 0 ? (
                <div className="flex min-h-[12rem] items-center justify-center p-6">
                    <Text variant="small" className="text-foreground-muted">
                        {NO_SETTLEMENT_HISTORY_SUBTITLE}
                    </Text>
                </div>
            ) : view === "chart" ? (
                <AccountSettlementChart
                    history={history}
                    selectedCycleUuid={selectedCycleUuid}
                    onSelectCycle={onSelectCycle}
                />
            ) : (
                <AccountSettlementTable
                    history={history}
                    selectedCycleUuid={selectedCycleUuid}
                    onSelectCycle={onSelectCycle}
                />
            )}
        </section>
    );
}

function AccountSettlementChart({
    history,
    selectedCycleUuid,
    onSelectCycle
}: {
    history: ClientAccountSettlementEvaluation[];
    selectedCycleUuid: string | null;
    onSelectCycle: (cycleUuid: string) => void;
}): React.ReactElement {
    const points = useMemo(
        () =>
            [...history]
                .slice()
                .reverse()
                .map((entry) => ({
                    ...entry,
                    label: `C${entry.cycleNumber}`,
                    distributed: toNumber(entry.distributedAmount)
                })),
        [history]
    );

    return (
        <motion.div
            className={cn(
                "h-[14rem] px-2 py-4",
                SETTLEMENT_CHART_SURFACE_CLASS
            )}
            role="img"
            aria-label="Bar chart of selected account distributed amounts by settlement cycle. Use table view for keyboard selection."
            onMouseDown={(event) => event.preventDefault()}
        >
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart
                    data={points}
                    margin={{ top: 10, right: 18, left: 8, bottom: 8 }}
                    accessibilityLayer={false}
                    onClick={(state) => {
                        const payload = (
                            state as
                                | {
                                      activePayload?: Array<{
                                          payload?: ClientAccountSettlementEvaluation & {
                                              distributed: number;
                                          };
                                      }>;
                                  }
                                | undefined
                        )?.activePayload?.[0]?.payload as
                            | (ClientAccountSettlementEvaluation & {
                                  distributed: number;
                              })
                            | undefined;
                        if (payload?.cycleUuid)
                            onSelectCycle(payload.cycleUuid);
                    }}
                >
                    <CartesianGrid
                        horizontal
                        vertical={false}
                        stroke="rgb(var(--color-foreground-base) / 0.08)"
                    />
                    <XAxis
                        dataKey="label"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) =>
                            formatUsdCompact(Number(value))
                        }
                    />
                    <Tooltip
                        cursor={{
                            fill: "rgb(var(--color-foreground-base) / 0.06)",
                            stroke: "none",
                            strokeWidth: 0
                        }}
                        content={({ active, payload }) => {
                            if (!active || !payload?.[0]?.payload) return null;
                            const entry = payload[0]
                                .payload as ClientAccountSettlementEvaluation & {
                                distributed: number;
                            };
                            return <AccountSettlementTooltip entry={entry} />;
                        }}
                    />
                    <Bar dataKey="distributed" radius={0} activeBar={false}>
                        {points.map((point) => (
                            <Cell
                                key={point.cycleUuid}
                                fill={resultColor(point.result)}
                                opacity={
                                    selectedCycleUuid &&
                                    selectedCycleUuid !== point.cycleUuid
                                        ? 0.45
                                        : 1
                                }
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </motion.div>
    );
}

function AccountSettlementTooltip({
    entry
}: {
    entry: ClientAccountSettlementEvaluation & { distributed: number };
}): React.ReactElement {
    return (
        <Text
            variant="small"
            render={<div />}
            className="bg-surface-inverse-raised text-foreground-inverse px-3 py-2 min-w-[200px]"
        >
            <Text
                variant="label"
                render={<div />}
                className="text-foreground-inverse-neutral mb-1"
            >
                Cycle {entry.cycleNumber} - {formatDate(entry.runAt)}
            </Text>
            <Text
                variant="number"
                render={<div />}
                className="text-foreground-inverse"
            >
                {formatUsd(entry.distributed)}
            </Text>
            <div className="mt-2 flex gap-2">
                <ResultBadge result={entry.result} compact />
            </div>
        </Text>
    );
}

function AccountSettlementTable({
    history,
    selectedCycleUuid,
    onSelectCycle
}: {
    history: ClientAccountSettlementEvaluation[];
    selectedCycleUuid: string | null;
    onSelectCycle: (cycleUuid: string) => void;
}): React.ReactElement {
    return (
        <div className="overflow-x-auto">
            <div className="min-w-[44rem]">
                <div className="grid grid-cols-[0.7fr_1.2fr_1fr_1fr_1fr] gap-4 border-b border-line-base px-4 py-3 text-foreground-muted">
                    <HeaderCell>Cycle</HeaderCell>
                    <HeaderCell>Account result</HeaderCell>
                    <HeaderCell align="right">Net reward</HeaderCell>
                    <HeaderCell align="right">Distributed</HeaderCell>
                    <HeaderCell align="right">Date</HeaderCell>
                </div>
                <div className="divide-y divide-line-base">
                    {history.map((entry) => (
                        <button
                            key={entry.uuid}
                            type="button"
                            aria-current={selectedCycleUuid === entry.cycleUuid}
                            onClick={() => onSelectCycle(entry.cycleUuid)}
                            className={cn(
                                "grid w-full grid-cols-[0.7fr_1.2fr_1fr_1fr_1fr] gap-4 border-l-2 py-3 pl-3 pr-4 text-left transition-colors",
                                selectedCycleUuid === entry.cycleUuid
                                    ? "border-l-accent-base bg-surface-base"
                                    : "border-l-transparent hover:bg-surface-sunken"
                            )}
                        >
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                {entry.cycleNumber}
                            </Text>
                            <div>
                                <ResultBadge result={entry.result} compact />
                            </div>
                            <NumberCell>
                                {formatUsd(toNumber(entry.netRewardAmount))}
                            </NumberCell>
                            <NumberCell>
                                {formatUsd(toNumber(entry.distributedAmount))}
                            </NumberCell>
                            <Text
                                variant="label"
                                render={<span />}
                                className="text-right text-foreground-muted"
                            >
                                {formatDate(entry.runAt)}
                            </Text>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AccountDetailPanel({
    detail,
    isLoading,
    view,
    selectedCycleUuid,
    onViewChange,
    onSelectCycle
}: {
    detail: ClientAccountDetailResponse | null;
    isLoading: boolean;
    view: PeriodView;
    selectedCycleUuid: string | null;
    onViewChange: (view: PeriodView) => void;
    onSelectCycle: (cycleUuid: string) => void;
}): React.ReactElement {
    if (isLoading && !detail) {
        return (
            <section
                className="border border-line-base bg-surface-raised p-6"
                aria-busy="true"
                aria-label="Loading account detail"
            >
                <LoadingBars />
            </section>
        );
    }

    if (!detail) {
        return (
            <section className="border border-line-base bg-surface-raised p-6">
                <Heading
                    title="No account selected"
                    titleVariant="h2"
                    subtitle="Select an address from the account list."
                    subtitleVariant="small"
                />
            </section>
        );
    }

    const selectedPeriod = selectedCycleUuid
        ? detail.selectedSettlementPeriod?.cycleUuid === selectedCycleUuid
            ? detail.selectedSettlementPeriod
            : (detail.settlementHistory.find(
                  (period) => period.cycleUuid === selectedCycleUuid
              ) ?? null)
        : detail.latestSettlementPeriod;

    return (
        <AnimatePresence mode="wait">
            <motion.section
                key={detail.account.uuid}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="border border-line-base bg-surface-raised"
                aria-label="Selected account"
            >
                <div className="grid gap-4 border-b border-line-base p-4 lg:grid-cols-[1.4fr_repeat(3,minmax(8rem,1fr))]">
                    <div className="min-w-0">
                        <Text variant="label" className="text-foreground-muted">
                            Account
                        </Text>
                        <div className="mt-2 flex min-w-0 items-start gap-2">
                            <Text
                                variant="number"
                                render={<code />}
                                className="min-w-0 flex-1 break-all text-foreground-base"
                            >
                                {detail.account.address}
                            </Text>
                            <CopyAddressButton
                                address={detail.account.address}
                            />
                        </div>
                    </div>
                    <DetailMetric
                        label="Balance"
                        value={formatUsd(
                            toNumber(detail.account.currentBalance)
                        )}
                    />
                    <DetailMetric
                        label="Rewards distributed"
                        value={formatUsd(
                            toNumber(detail.account.totalRewardsDistributed)
                        )}
                    />
                    <div>
                        <Text variant="label" className="text-foreground-muted">
                            Latest result
                        </Text>
                        <div className="mt-2">
                            {detail.latestSettlementPeriod ? (
                                <ResultBadge
                                    result={
                                        detail.latestSettlementPeriod.result
                                    }
                                />
                            ) : (
                                <OutcomeBadge tone="neutral">
                                    No History
                                </OutcomeBadge>
                            )}
                        </div>
                    </div>
                </div>

                <div className="border-b border-line-base p-4">
                    <AccountSettlementExplorer
                        history={detail.settlementHistory}
                        latest={detail.latestSettlementPeriod}
                        selectedPeriod={selectedPeriod}
                        view={view}
                        selectedCycleUuid={selectedCycleUuid}
                        onViewChange={onViewChange}
                        onSelectCycle={onSelectCycle}
                    />
                </div>

                <div className="grid gap-6 p-4 2xl:grid-cols-[minmax(0,1.1fr)_minmax(24rem,0.9fr)]">
                    <div className="flex min-w-0 flex-col gap-6">
                        <AccountSettlementSummary evaluation={selectedPeriod} />
                        <DecisionTrail items={detail.decisionTrail} />
                    </div>
                    <div className="flex min-w-0 flex-col gap-6">
                        <ProgramSurfaces surfaces={detail.programSurfaces} />
                        <ScreeningChecks checks={detail.screeningChecks} />
                    </div>
                </div>
            </motion.section>
        </AnimatePresence>
    );
}

function AccountSettlementSummary({
    evaluation
}: {
    evaluation: ClientAccountSettlementEvaluation | null;
}): React.ReactElement {
    if (!evaluation) {
        return (
            <section className={cn(ACCOUNT_DETAIL_SECTION_CLASS, "p-4")}>
                <Heading
                    title="No Settlement History"
                    titleVariant="h2"
                    subtitle={NO_SETTLEMENT_HISTORY_SUBTITLE}
                    subtitleVariant="small"
                />
            </section>
        );
    }

    return (
        <section className={ACCOUNT_DETAIL_SECTION_CLASS}>
            <div className="border-b border-line-base p-4">
                <Heading
                    eyebrow={`Cycle ${evaluation.cycleNumber}`}
                    title={
                        evaluation.safeSummary ?? resultLabel(evaluation.result)
                    }
                    titleVariant="h2"
                    className="gap-1"
                />
                <Text
                    variant="label"
                    className="mt-2 block text-foreground-muted"
                >
                    {formatDate(evaluation.runAt)}
                </Text>
            </div>
            <div className="grid gap-3 p-4 md:grid-cols-4">
                <DetailMetric
                    label="Observed"
                    value={formatMaybeUsd(evaluation.observedBalance)}
                />
                <DetailMetric
                    label="Eligible"
                    value={formatMaybeUsd(evaluation.eligibleBalance)}
                />
                <DetailMetric
                    label="Net reward"
                    value={formatUsd(toNumber(evaluation.netRewardAmount))}
                />
                <DetailMetric
                    label="Distributed"
                    value={formatUsd(toNumber(evaluation.distributedAmount))}
                />
            </div>
        </section>
    );
}

type DecisionTrailTone =
    | "disabled"
    | "neutral"
    | "blocked"
    | "warning"
    | "positive";

function isRoutineFeeSplit(item: ClientAccountDecisionTrailItem): boolean {
    return (
        item.ruleType === "fee-split" &&
        item.status === "Applied" &&
        item.effect === "ReducedReward"
    );
}

function getDecisionTrailTone(
    item: ClientAccountDecisionTrailItem
): DecisionTrailTone {
    if (item.status === "Blocked") return "blocked";
    if (item.status === "Warning") return "warning";
    if (isRoutineFeeSplit(item)) return "neutral";
    if (item.status === "NotApplicable" || item.effect === "NoEffect")
        return "disabled";
    if (item.status === "Passed" || item.status === "Applied")
        return "positive";
    return "disabled";
}

const DECISION_TRAIL_ROW_CLASS: Record<DecisionTrailTone, string> = {
    disabled: "border-l-2 border-l-line-base bg-surface-sunken/60",
    neutral: "border-l-2 border-l-line-base bg-surface-sunken/60",
    blocked: "border-l-2 border-l-error-base bg-status-error-000",
    warning: "border-l-2 border-l-warning-base bg-status-warning-000",
    positive: "border-l-2 border-l-success-base bg-status-success-000"
};

const DECISION_TRAIL_BADGE_CLASS =
    "px-2 py-0.5 text-[11px] leading-4 md:text-xs md:leading-4 [&>svg]:!size-2.5";

function DecisionTrail({
    items
}: {
    items: ClientAccountDecisionTrailItem[];
}): React.ReactElement {
    return (
        <section className={ACCOUNT_DETAIL_SECTION_CLASS}>
            <div className="border-b border-line-base p-4">
                <Heading title="Decision trail" titleVariant="h2" />
            </div>
            {items.length === 0 ? (
                <div className="p-4">
                    <Text variant="small" className="text-foreground-muted">
                        No policy decision rows for the selected period.
                    </Text>
                </div>
            ) : (
                <div className="divide-y divide-line-base">
                    {items.map((item) => (
                        <DecisionTrailRow key={item.uuid} item={item} />
                    ))}
                </div>
            )}
        </section>
    );
}

function DecisionTrailRow({
    item
}: {
    item: ClientAccountDecisionTrailItem;
}): React.ReactElement {
    const tone = getDecisionTrailTone(item);
    const isDisabled = tone === "disabled";

    return (
        <div
            className={cn(
                "grid gap-3 p-4 md:grid-cols-[2rem_minmax(0,1fr)_auto]",
                DECISION_TRAIL_ROW_CLASS[tone]
            )}
        >
            <Text
                variant="number"
                className={cn(
                    isDisabled
                        ? "text-foreground-imprint"
                        : "text-foreground-muted"
                )}
            >
                {item.rank}
            </Text>
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <Text
                        variant="h3"
                        render={<h3 />}
                        className={cn(
                            isDisabled
                                ? "text-foreground-muted"
                                : "text-foreground-base"
                        )}
                    >
                        {categoryLabel(item.category)}
                    </Text>
                    <PolicyStatusBadge status={item.status} />
                    <EffectBadge item={item} />
                </div>
                <Text
                    variant="small"
                    className={cn(
                        "mt-1",
                        isDisabled
                            ? "text-foreground-imprint"
                            : "text-foreground-muted"
                    )}
                >
                    {item.publicSummary ?? ruleLabel(item.ruleType)}
                </Text>
            </div>
            <div className="text-right">
                {item.sourcePolicyUuid ? (
                    <Link
                        to={`/policies?${POLICY_PARAM}=${encodeURIComponent(item.sourcePolicyUuid)}`}
                        className={cn(
                            "font-numeric-mono text-xs underline-offset-2 hover:underline",
                            isDisabled
                                ? "text-foreground-imprint hover:text-foreground-muted"
                                : "text-accent-base"
                        )}
                    >
                        {truncateAddress(item.sourcePolicyUuid, 6, 6)}
                    </Link>
                ) : (
                    <Text
                        variant="label"
                        render={<span />}
                        className="text-foreground-imprint"
                    >
                        policy
                    </Text>
                )}
            </div>
        </div>
    );
}

function ProgramSurfaces({
    surfaces
}: {
    surfaces: ClientAccountDetailResponse["programSurfaces"];
}): React.ReactElement {
    return (
        <section className={ACCOUNT_DETAIL_SECTION_CLASS}>
            <div className="border-b border-line-base p-4">
                <Heading title="Program Surfaces" titleVariant="h2" />
            </div>
            {surfaces.length === 0 ? (
                <div className="p-4">
                    <Text variant="small" className="text-foreground-muted">
                        No linked Program Surfaces.
                    </Text>
                </div>
            ) : (
                <div className="divide-y divide-line-base">
                    {surfaces.map((surface) => (
                        <div
                            key={surface.positionUuid}
                            className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 p-4"
                        >
                            <div className="min-w-0">
                                <Text
                                    variant="body"
                                    className="truncate text-foreground-base"
                                >
                                    {surface.surfaceName}
                                </Text>
                                <Text
                                    variant="label"
                                    className="mt-1 block text-foreground-muted"
                                >
                                    {surface.surfaceCategoryName ??
                                        surface.type}
                                </Text>
                            </div>
                            <div className="text-right">
                                <Text
                                    variant="number"
                                    className="block text-foreground-base"
                                >
                                    {formatUsd(toNumber(surface.balance))}
                                </Text>
                                <Text
                                    variant="label"
                                    className="mt-1 block text-foreground-muted"
                                >
                                    {formatUsd(toNumber(surface.totalRewards))}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function ScreeningChecks({
    checks
}: {
    checks: ClientAccountDetailResponse["screeningChecks"];
}): React.ReactElement {
    return (
        <section className={ACCOUNT_DETAIL_SECTION_CLASS}>
            <div className="border-b border-line-base p-4">
                <Heading title="Screening" titleVariant="h2" />
            </div>
            {checks.length === 0 ? (
                <div className="p-4">
                    <Text variant="small" className="text-foreground-muted">
                        No screening checks recorded.
                    </Text>
                </div>
            ) : (
                <div className="divide-y divide-line-base">
                    {checks.map((check) => (
                        <div key={check.uuid} className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <Text
                                    variant="body"
                                    className="text-foreground-base"
                                >
                                    {check.provider}
                                </Text>
                                {check.severity === "Critical" ? (
                                    <AlertBadge severity="critical">
                                        {screeningLabel(check.status)}
                                    </AlertBadge>
                                ) : (
                                    <OutcomeBadge tone="neutral">
                                        {screeningLabel(check.status)}
                                    </OutcomeBadge>
                                )}
                            </div>
                            <Text
                                variant="small"
                                className="mt-2 text-foreground-muted"
                            >
                                {check.safeSummary ??
                                    (check.categories.join(", ") || "Clear")}
                            </Text>
                            <Text
                                variant="label"
                                className="mt-2 block text-foreground-muted"
                            >
                                {formatDate(check.checkedAt)}
                            </Text>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function DetailMetric({
    label,
    value
}: {
    label: string;
    value: string;
}): React.ReactElement {
    return (
        <div className="min-w-0">
            <Text variant="label" className="text-foreground-muted">
                {label}
            </Text>
            <Text
                variant="number"
                className="mt-2 block truncate text-foreground-base"
            >
                {value}
            </Text>
        </div>
    );
}

function HeaderCell({
    children,
    align = "left"
}: {
    children: React.ReactNode;
    align?: "left" | "right";
}): React.ReactElement {
    return (
        <Text
            variant="label"
            render={<span />}
            className={cn(align === "right" && "text-right")}
        >
            {children}
        </Text>
    );
}

function NumberCell({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Text
            variant="number"
            render={<span />}
            className="text-right text-foreground-base"
        >
            {children}
        </Text>
    );
}

function LoadingBars({ rows = 5 }: { rows?: number }): React.ReactElement {
    return (
        <div className="animate-pulse space-y-3">
            {Array.from({ length: rows }).map((_, index) => (
                <div
                    key={index}
                    className="h-4 bg-surface-sunken"
                    style={{ width: `${index % 2 === 0 ? 80 : 55}%` }}
                />
            ))}
        </div>
    );
}

function ErrorShell({
    title,
    message
}: {
    title: string;
    message: string;
}): React.ReactElement {
    return (
        <section className="border border-line-base bg-surface-raised p-6">
            <Heading
                title={title}
                titleVariant="h2"
                subtitle={message}
                subtitleVariant="small"
                className="gap-2"
            />
        </section>
    );
}

function ResultBadge({
    result,
    compact = false
}: {
    result: ParticipantAccountSettlementResult;
    compact?: boolean;
}): React.ReactElement {
    const tone = result === "Rewarded" ? "success" : "danger";

    return (
        <OutcomeBadge tone={tone}>
            {compact ? resultShortLabel(result) : resultLabel(result)}
        </OutcomeBadge>
    );
}

function PolicyStatusBadge({
    status
}: {
    status: ClientAccountDecisionTrailItem["status"];
}): React.ReactElement {
    if (status === "NotApplicable") {
        return (
            <OutcomeBadge tone="neutral" className={DECISION_TRAIL_BADGE_CLASS}>
                Not applicable
            </OutcomeBadge>
        );
    }

    const tone =
        status === "Blocked"
            ? "danger"
            : status === "Warning"
              ? "warning"
              : status === "Passed" || status === "Applied"
                ? "success"
                : "neutral";

    return (
        <OutcomeBadge tone={tone} className={DECISION_TRAIL_BADGE_CLASS}>
            {policyStatusLabel(status)}
        </OutcomeBadge>
    );
}

function EffectBadge({
    item
}: {
    item: ClientAccountDecisionTrailItem;
}): React.ReactElement {
    if (isRoutineFeeSplit(item)) {
        return (
            <OutcomeBadge tone="neutral" className={DECISION_TRAIL_BADGE_CLASS}>
                Fee applied
            </OutcomeBadge>
        );
    }

    if (item.effect === "NoEffect") {
        return (
            <OutcomeBadge tone="neutral" className={DECISION_TRAIL_BADGE_CLASS}>
                No effect
            </OutcomeBadge>
        );
    }

    const tone =
        item.effect === "BlockedReward"
            ? "danger"
            : item.effect === "ScreeningHold"
              ? "warning"
              : "warning";

    return (
        <OutcomeBadge tone={tone} className={DECISION_TRAIL_BADGE_CLASS}>
            {effectLabel(item.effect)}
        </OutcomeBadge>
    );
}

function policyStatusLabel(
    status: ClientAccountDecisionTrailItem["status"]
): string {
    switch (status) {
        case "NotApplicable":
            return "Not applicable";
        case "Blocked":
            return "Blocked";
        case "Warning":
            return "Warning";
        case "Passed":
            return "Passed";
        case "Applied":
            return "Applied";
    }
}

function resultColor(result: ParticipantAccountSettlementResult): string {
    switch (result) {
        case "Rewarded":
            return "rgb(var(--color-success-base))";
        case "NotRewarded":
            return "rgb(var(--color-error-base))";
    }
}

function toNumber(value: string | null | undefined): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

function formatUsd(value: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: value >= 1000 ? 0 : 2
    }).format(value);
}

function formatUsdCompact(value: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        maximumFractionDigits: 1
    }).format(value);
}

function formatMaybeUsd(value: string | null): string {
    return value === null ? "N/A" : formatUsd(toNumber(value));
}

function formatDate(value: string): string {
    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}

function CopyAddressButton({
    address
}: {
    address: string;
}): React.ReactElement {
    const [copied, setCopied] = useState(false);

    const handleCopy = (): void => {
        void navigator.clipboard.writeText(address).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Button
            variant="transparent"
            interaction="default"
            size="xs"
            onClick={handleCopy}
            className="shrink-0"
        >
            {copied ? "Copied" : "Copy"}
        </Button>
    );
}

function truncateAddress(value: string, leading = 6, trailing = 6): string {
    if (value.length <= leading + trailing + 3) return value;
    return `${value.slice(0, leading)}...${value.slice(-trailing)}`;
}

function resultLabel(result: ParticipantAccountSettlementResult): string {
    switch (result) {
        case "Rewarded":
            return "Rewarded";
        case "NotRewarded":
            return "Not rewarded";
    }
}

function resultShortLabel(result: ParticipantAccountSettlementResult): string {
    return resultLabel(result);
}

function categoryLabel(
    category: ClientAccountDecisionTrailItem["category"]
): string {
    switch (category) {
        case "CapsAndLimits":
        case "caps":
            return "Caps & Limits";
        case "Compliance":
        case "compliance":
            return "Compliance";
        case "Eligibility":
        case "eligibility":
            return "Eligibility";
        case "Allocation":
        case "allocation":
            return "Allocation";
        case "Scheduling":
        case "scheduling":
            return "Scheduling";
    }
}

function ruleLabel(ruleType: string): string {
    return ruleType
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

function effectLabel(effect: ClientAccountDecisionTrailItem["effect"]): string {
    switch (effect) {
        case "BlockedReward":
            return "Blocked reward";
        case "CappedBalance":
            return "Capped balance";
        case "ReducedReward":
            return "Reduced reward";
        case "ScreeningHold":
            return "Screening hold";
        case "NoEffect":
            return "No effect";
    }
}

function screeningLabel(
    status: ClientAccountDetailResponse["screeningChecks"][number]["status"]
): string {
    switch (status) {
        case "UnderReview":
            return "Under review";
        case "Blocked":
        case "Clear":
        case "Failed":
        case "Expired":
        default:
            return status;
    }
}
